/**
 * Thin typed web API client for the AI Review feature (web/v2).
 *
 * Mirrors `createImportsClient` (`imports.client.ts`) exactly — same
 * `makeAuthedFetch` default (single-flight 401 -> POST /auth/refresh ->
 * retry-once, shared module-scoped state with `AuthGuard`'s own `useMe()`
 * call), same `credentials: "include"` cookie auth, same error shape
 * (`AiReviewApiError` mirrors `ImportsApiError`: `status` + `url`, optional
 * message). Without the default `fetchImpl`, a run's ~2500ms poll loop
 * (`useAiReviewRun.ts`) could sit through the 1h access-token cookie
 * expiring mid-poll and throw a raw, unrefreshed 401 — the exact bug fixed
 * 2026-07-05 for `imports.client.ts` (see its docstring).
 *
 * Every function is typed end-to-end against `@nu-assist/contracts`, so a
 * backend schema change surfaces at `tsc` time across every consumer.
 */

import type {
  AiReviewAccepted,
  AiReviewContactPrompt,
  AiReviewCreateRequest,
  AiReviewResult,
  CriteriaSuggestionEventRequest,
  CriteriaSuggestionsRequest,
  CriteriaSuggestionsResponse,
  components,
} from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";
import { useQuery } from "@tanstack/react-query";
import { clearStoredSelectedImport } from "../lib/selectedImportStorage";

/** Minimal `fetch`-shaped function — `makeAuthedFetch(...)` by default (see
 *  `createAiReviewClient`); inject a different one (e.g. a test double) to
 *  override. */
export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface AiReviewClientConfig {
  /** API origin (e.g. `import.meta.env.VITE_API_URL`). */
  apiBase: string;
  /** Defaults to `makeAuthedFetch({ apiBase, onSessionExpired })` — a hard
   *  session expiry (refresh cookie dead) hard-redirects to `/login`, same
   *  outcome `AuthGuard` already enforces independently. Inject to override
   *  (tests pass a plain mock `fetch`). */
  fetchImpl?: FetchLike;
}

/** `GET …/contacts/{id}/call` — the model call behind one contact's scores. */
export type AiReviewContactCall = components["schemas"]["AiReviewContactCall"];
export type AiReviewCallAttempt = components["schemas"]["AiReviewCallAttempt"];

/** The server's own words on a refusal: `{"detail": {"reason", "message"}}`. */
export interface AiReviewErrorDetail {
  reason: string | null;
  message: string | null;
  /** Seconds, from `Retry-After` (429). */
  retryAfter: number | null;
}

export class AiReviewApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message?: string,
    public readonly detail: AiReviewErrorDetail = { reason: null, message: null, retryAfter: null }
  ) {
    super(message ?? `AI Review API request failed (${status}) for ${url}`);
    this.name = "AiReviewApiError";
    Object.setPrototypeOf(this, AiReviewApiError.prototype);
  }
}

async function readErrorDetail(res: Response): Promise<AiReviewErrorDetail> {
  const retry = Number(res.headers?.get?.("retry-after"));
  const retryAfter = Number.isFinite(retry) && retry > 0 ? retry : null;
  try {
    const body = (await res.json()) as { detail?: unknown };
    const d = body?.detail;
    if (d && typeof d === "object") {
      const { reason, message } = d as { reason?: unknown; message?: unknown };
      return {
        reason: typeof reason === "string" ? reason : null,
        message: typeof message === "string" ? message : null,
        retryAfter,
      };
    }
    return { reason: null, message: typeof d === "string" ? d : null, retryAfter };
  } catch {
    return { reason: null, message: null, retryAfter };
  }
}

const PREFIX = "/api/web/v2/ai-reviews";

export function createAiReviewClient(cfg: AiReviewClientConfig) {
  // Constructed once here, not per-request; safe to build eagerly because
  // `authedFetch`'s internal `fetch(...)` calls resolve `globalThis.fetch`
  // at CALL time (a bare identifier, not a captured reference), so MSW's
  // `server.listen()` patch — applied after this client is constructed at
  // module-eval time — is still honored. See `imports.client.ts`'s own note.
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: cfg.apiBase,
      onSessionExpired: () => {
        // The shared import selection is persisted and carries another
        // recruiter's data; a dead refresh token must not leave it for the
        // next person on this machine. See `clearStoredSelectedImport`.
        clearStoredSelectedImport();
        window.location.assign("/login");
      },
    });
  const doFetch: FetchLike = (input, init) => authedFetch(input, init);

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${cfg.apiBase}${path}`;
    const res = await doFetch(url, {
      method,
      credentials: "include",
      headers: body !== undefined ? { "content-type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      throw new AiReviewApiError(res.status, url, undefined, await readErrorDetail(res));
    }
    return (await res.json()) as T;
  }

  return {
    /** POST /ai-reviews — enqueue a scoring run (202). */
    createRun(body: AiReviewCreateRequest): Promise<AiReviewAccepted> {
      return request<AiReviewAccepted>("POST", PREFIX, body);
    },

    /** POST /ai-reviews/criteria-suggestions — criteria suggested from the
     *  decisions in the list's Thrive search (sync, up to ~55 s). Nothing is
     *  saved; errors carry `detail.reason` (`import_has_no_search`,
     *  `thrive_unavailable`, `thrive_timeout`, `invalid_output`,
     *  `model_unavailable`, `suggestion_timeout`). */
    suggestCriteria(body: CriteriaSuggestionsRequest): Promise<CriteriaSuggestionsResponse> {
      return request<CriteriaSuggestionsResponse>("POST", `${PREFIX}/criteria-suggestions`, body);
    },

    /** POST /ai-reviews/criteria-suggestions/events — suggestions `added` to
     *  the form or `removed` from it (204, nothing returned). Measures how the
     *  suggestions are used; callers fire and forget. */
    async recordSuggestionEvent(body: CriteriaSuggestionEventRequest): Promise<void> {
      const url = `${cfg.apiBase}${PREFIX}/criteria-suggestions/events`;
      const res = await doFetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw new AiReviewApiError(res.status, url, undefined, await readErrorDetail(res));
      }
    },

    /** GET /ai-reviews/{id} — poll target (pending -> processing -> terminal). */
    getRun(
      reviewId: string,
      params?: { sort_key?: string; sort_dir?: "asc" | "desc" },
    ): Promise<AiReviewResult> {
      const qs = params
        ? new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][],
          ).toString()
        : "";
      return request<AiReviewResult>("GET", `${PREFIX}/${reviewId}${qs ? `?${qs}` : ""}`);
    },

    /** GET /ai-reviews/by-import/{id} — the import's latest scored run (for
     *  the export-time "view scores + reasons" panel). Resolves to `null`
     *  (not a throw) on 404 so a never-reviewed import renders no panel
     *  instead of surfacing an error. */
    async getLatestByImport(importId: string): Promise<AiReviewResult | null> {
      try {
        return await request<AiReviewResult>("GET", `${PREFIX}/by-import/${importId}`);
      } catch (err) {
        if (err instanceof AiReviewApiError && err.status === 404) return null;
        throw err;
      }
    },

    /** GET /ai-reviews/{id}/contacts/{contactId}/prompt — the prompt the
     *  contact was scored with, rebuilt server-side (prompt viewer). Every
     *  "cannot show it" case is a 200 with `status: "unavailable"`; a 404
     *  (contact not scored in this run) throws `AiReviewApiError`. */
    getContactPrompt(reviewId: string, contactId: string): Promise<AiReviewContactPrompt> {
      return request<AiReviewContactPrompt>(
        "GET",
        `${PREFIX}/${encodeURIComponent(reviewId)}/contacts/${encodeURIComponent(contactId)}/prompt`
      );
    },

    /** GET /ai-reviews/{id}/contacts/{contactId}/call — the recorded model
     *  call behind this contact's scores (model, route, tokens, attempts…).
     *  "Not recorded" is a 200 with `status: "unavailable"`. */
    getContactCall(reviewId: string, contactId: string): Promise<AiReviewContactCall> {
      return request<AiReviewContactCall>(
        "GET",
        `${PREFIX}/${encodeURIComponent(reviewId)}/contacts/${encodeURIComponent(contactId)}/call`
      );
    },

    /** POST /ai-reviews/{id}/apply — build the DERIVED import from a
     *  succeeded run. Asynchronous: the API flips the row to `applying`,
     *  enqueues the worker and answers `202 AiReviewAccepted`, so the caller
     *  must keep polling `getRun` until `applied` or `failed`. An
     *  already-applied run answers 202 too, with `status: "applied"` — an
     *  idempotent repeat, not an error.
     *
     *  The `output_import_id` that names a file you can OPEN is the one on
     *  `getRun`'s `AiReviewResult`, narrowed to a `ready` child. The 202 has a
     *  field of the same name and it is NOT that — on this endpoint it is
     *  always null, since apply announces no new row. Two fields, one name; the
     *  schema descriptions carry the full distinction.
     *
     *  `outputFilename` is the name the derived import will carry. It rides on
     *  APPLY, not on create: nothing persists it between the two calls, and the
     *  name belongs to the file, which is born here. Omitted -> the worker
     *  derives `<original stem>_AI_review<parent extension>`. */
    applyRun(reviewId: string, outputFilename?: string | null): Promise<AiReviewAccepted> {
      return request<AiReviewAccepted>("POST", `${PREFIX}/${reviewId}/apply`, {
        output_filename: outputFilename?.trim() || null,
      });
    },
  };
}

export type AiReviewClient = ReturnType<typeof createAiReviewClient>;

/** `GET /ai-reviews/{id}/contacts/{contactId}/prompt` — the prompt one
 *  contact was scored with (the prompt viewer). Idle until both ids are set.
 *  A scored contact's prompt does not change, so it is cached for the session
 *  slot; no retry: a 404 means "not scored in this run", which retrying does
 *  not change, and every other "cannot show it" is a 200. */
export function useAiReviewContactPrompt({
  client,
  reviewId,
  contactId,
}: {
  client: AiReviewClient;
  reviewId: string | null;
  contactId: string | null;
}) {
  return useQuery<AiReviewContactPrompt, AiReviewApiError>({
    queryKey: ["ai-review", "contact-prompt", reviewId, contactId],
    queryFn: () => client.getContactPrompt(reviewId as string, contactId as string),
    enabled: reviewId != null && contactId != null,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** `GET /ai-reviews/{id}/contacts/{contactId}/call` — same caching rules as
 *  the prompt: a recorded call never changes. `enabled` lets a caller that
 *  may not show it (the audience switch) skip the request. */
export function useAiReviewContactCall({
  client,
  reviewId,
  contactId,
  enabled = true,
}: {
  client: AiReviewClient;
  reviewId: string | null;
  contactId: string | null;
  enabled?: boolean;
}) {
  return useQuery<AiReviewContactCall, AiReviewApiError>({
    queryKey: ["ai-review", "contact-call", reviewId, contactId],
    queryFn: () => client.getContactCall(reviewId as string, contactId as string),
    enabled: enabled && reviewId != null && contactId != null,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
